import { FC, ReactNode } from 'react'
import { ScopeProvider } from 'jotai-scope'

import {
  GreyGrey20,
  GreyGrey30,
  GreyGrey60,
  GreyWhite,
  Center,
  Flex
} from '@sequencehq/core-components'

import {
  NodeTimelineVariant,
  nodeTimelineVariantAtom,
  useNodeTimelineVariant
} from './NodeTimeline.atoms'
import { TimelineUserAction } from './TimelineUserAction'
import { match } from 'ts-pattern'

const Wrapper: FC<{
  children: ReactNode
  'data-testid'?: string
  padding?: string
  width?: string
  variant?: NodeTimelineVariant
}> = ({
  children,
  'data-testid': dataTestId,
  padding = '32px',
  width,
  variant = 'DEFAULT'
}) => {
  return (
    <ScopeProvider atoms={[[nodeTimelineVariantAtom, variant]]}>
      <Flex
        flexDir="column"
        // On mobile the timeline drops its fixed width and fills the pane; the
        // tighter padding avoids horizontal overflow on a phone.
        padding={{ base: '16px', md: padding }}
        width={{ base: '100%', md: width ?? `calc(632px + ${padding} * 2)` }}
        maxWidth="100%"
        marginX="auto"
        data-testid={dataTestId}
      >
        {children}
      </Flex>
    </ScopeProvider>
  )
}

const Item: FC<{
  children: ReactNode
  icon: ReactNode
  showStem?: boolean
  highlightIcon?: boolean
  iconPositionOffset?: {
    x?: string
    y?: string
  }
  'data-testid'?: string
}> = ({
  children,
  icon,
  showStem = true,
  highlightIcon,
  iconPositionOffset,
  'data-testid': dataTestId
}) => {
  const variant = useNodeTimelineVariant()

  return (
    <Flex
      data-testid={dataTestId}
      gap="8px"
      paddingBottom={match(variant)
        .with('DEFAULT', () => '8px')
        .with('INSPECTOR', () => '0px')
        .exhaustive()}
      position="relative"
    >
      {/* The icon rail and connecting stem make up the "visual timeline". On
          mobile they're hidden so items stack full-width top to bottom. */}
      <Flex
        flexDir="column"
        alignItems="center"
        zIndex={2}
        display={{ base: 'none', md: 'flex' }}
      >
        <Center
          boxSize="24px"
          borderRadius="6px"
          backgroundColor={highlightIcon ? GreyGrey20 : GreyWhite}
          flexShrink="0"
          position="relative"
          left={iconPositionOffset?.x}
          top={iconPositionOffset?.y}
        >
          <Flex width="16px" height="16px" color={GreyGrey60}>
            {icon}
          </Flex>
        </Center>
      </Flex>

      {showStem && (
        <Flex
          width="2px"
          height="calc(100% + 8px)"
          background={GreyGrey30}
          position="absolute"
          top={0}
          // Centre the 2px stem on the 24px icon anchor (12px centre - 1px)
          left="11px"
          zIndex={1}
          display={{ base: 'none', md: 'flex' }}
        />
      )}

      <Flex flexDir="column" flex="1" minWidth={0}>
        {children}
      </Flex>
    </Flex>
  )
}

const Title: FC<{
  children: ReactNode
  marginTop?: string
}> = ({ children, marginTop = '4px' }) => {
  const variant = useNodeTimelineVariant()

  return (
    <Flex flexDir="column">
      <Flex
        marginTop={marginTop}
        marginBottom={match(variant)
          .with('DEFAULT', () => '8px')
          .with('INSPECTOR', () => '12px')
          .exhaustive()}
        paddingRight="10px"
      >
        <Flex>{children}</Flex>
      </Flex>
    </Flex>
  )
}

export const NodeTimeline = Object.assign(Wrapper, {
  Item,
  Title,
  UserAction: TimelineUserAction
})
